import {
	readCallBookingConfig,
	findCallBookingFields,
	earliestBookableDate,
	latestBookableDate,
	isOpenOn,
	toIsoDate,
	slotToMinutes,
	describeSlot,
	slotOptionLabel,
	guestTimeDescription,
	slotNearestLocalTime,
	guestTimeZone,
	lookupGuestGeo,
	guestPlace,
	timeZoneAbbreviation,
	guestZoneAbbreviation,
	prettyTimeZoneName,
} from '../../assets/scripts/utils/call-booking.js';

/**
 * Diagnostic logger for the multi-step "Start Planning" (legacy) Gravity Form.
 *
 * WHY THIS EXISTS
 * The form pages via Gravity Forms' hidden-iframe AJAX method. Each Next/Previous
 * is a form submit guarded by `window['gf_submitting_{formId}']`: if that flag is
 * already `true`, GF silently swallows the submit (no error, buttons appear dead).
 * GF only resets the flag inside the iframe's `load` handler, and ONLY when the
 * response contains the `GF_AJAX_POSTBACK` marker. So the flag gets stuck whenever
 * the iframe request is killed (mobile backgrounding/idle) or returns a non-GF
 * page (page cache, expired nonce/session, redirect, 4xx/5xx). This logger records
 * the full pagination lifecycle to a rolling sessionStorage buffer so an
 * intermittent, hard-to-reproduce lockup can be captured after the fact.
 *
 * HOW TO USE (share these with whoever can reproduce it)
 *   - Live console output:  add `?gfdebug=1` to the URL, or run `gfDiag.enable()`.
 *   - After it breaks:       run `gfDiag.copy()` (or `gfDiag.dump()`) in the
 *                            browser console and send the output.
 *   - The buffer is always captured (even without gfdebug) for the current tab
 *     session, so `gfDiag.dump()` works retroactively.
 *
 * Look for `ev: "iframe-load"` with `isPostback: false` — that is the smoking gun
 * for a stuck flag, and its `snippet` tells you what came back instead (blank =
 * killed request, HTML page = cache/redirect/error).
 */
const GFFormDiag = (function () {
	const MAX_ENTRIES = 300;
	const STORAGE_KEY = 'gfFormDiagLog';

	let enabled = false;
	let attached = false;
	let formId = null;
	let component = null;
	let lastAt = Date.now();

	try {
		// Console output is ON by default while we chase the paging lockup. Silence
		// it with `?gfdebug=0` in the URL or `gfDiag.disable()`. The rolling buffer
		// is captured either way — retrieve it any time with `gfDiag.dump()`.
		enabled = !(/[?&]gfdebug=0/.test(window.location.search) || window.localStorage.getItem('gfDiag') === '0');
	} catch (e) {
		enabled = true;
	}

	try {
		console.log('[GFDiag] diagnostics loaded (gfDiag.disable() to silence, gfDiag.dump() to read the buffer)');
	} catch (e) {}

	function readBuffer() {
		try {
			return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '[]');
		} catch (e) {
			return [];
		}
	}

	function writeBuffer(buffer) {
		try {
			window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
		} catch (e) {}
	}

	function currentPage() {
		try {
			const el = document.querySelector('#gform_source_page_number_' + formId);
			return el ? el.value : null;
		} catch (e) {
			return null;
		}
	}

	function isSubmitting() {
		try {
			return window['gf_submitting_' + formId] === true;
		} catch (e) {
			return null;
		}
	}

	function log(event, extra) {
		try {
			const t = Date.now();
			const record = {
				t: new Date(t).toISOString(),
				dtMs: t - lastAt,
				ev: event,
				formId: formId,
				page: currentPage(),
				submitting: isSubmitting(),
				loading: component ? component.isLoading : null,
				vis: document.visibilityState,
			};
			if (extra) {
				Object.assign(record, extra);
			}
			lastAt = t;

			const buffer = readBuffer();
			buffer.push(record);
			while (buffer.length > MAX_ENTRIES) {
				buffer.shift();
			}
			writeBuffer(buffer);

			if (enabled) {
				console.log('[GFDiag]', record.ev, record);
			}
		} catch (e) {}
	}

	function safeDetail(e) {
		try {
			if (!e || !e.detail) {
				return null;
			}
			return { formId: e.detail.formId, currentPage: e.detail.currentPage };
		} catch (err) {
			return null;
		}
	}

	function bindIframeWatcher() {
		function bind() {
			const frame = document.getElementById('gform_ajax_frame_' + formId);
			if (!frame) {
				return false;
			}
			if (frame.dataset.gfDiagBound === '1') {
				return true;
			}
			frame.dataset.gfDiagBound = '1';

			frame.addEventListener('load', function () {
				let isPostback = null;
				let htmlLen = null;
				let snippet = null;
				try {
					const doc = frame.contentDocument;
					const html = doc && doc.documentElement ? doc.documentElement.innerHTML : '';
					htmlLen = html.length;
					isPostback = html.indexOf('GF_AJAX_POSTBACK') >= 0;
					if (!isPostback) {
						// Capture what came back instead of a valid postback (blank =
						// killed request; a full page = cache/redirect/security/error).
						snippet = (doc && doc.title ? '[title: ' + doc.title + '] ' : '') + html.slice(0, 300);
					}
				} catch (err) {
					isPostback = 'unreadable (' + err.message + ')';
				}
				log('iframe-load', {
					isPostback: isPostback,
					htmlLen: htmlLen,
					snippet: snippet,
					submittingAfter: isSubmitting(),
				});

				// ACTIVE RECOVERY. The iframe firing `load` means the paging request
				// completed. If the response is NOT a valid Gravity Forms postback
				// (cache/redirect/security/error/oversized-header page) GF's own load
				// handler bails without clearing `gf_submitting_{formId}`, so every
				// further Next/Previous is silently swallowed — the lockup. Because the
				// request has demonstrably finished, it is safe to clear the flag right
				// now rather than wait up to 10s for the watchdog interval. Guarded on
				// `isPostback === false` (a real, readable non-postback) and the flag
				// actually being stuck, so it never touches the initial about:blank load
				// or a healthy postback.
				if (isPostback === false && isSubmitting() === true) {
					window['gf_submitting_' + formId] = false;
					if (component) {
						component.isLoading = false;
					}
					log('iframe-nonpostback-recovery', {
						note: 'cleared stuck gf_submitting after non-postback iframe response',
						snippet: snippet,
					});
				}
			});

			return true;
		}

		if (bind()) {
			return;
		}
		// The iframe may not exist yet on first init; retry briefly.
		let tries = 0;
		const interval = setInterval(function () {
			tries += 1;
			if (bind() || tries > 20) {
				clearInterval(interval);
			}
		}, 250);
	}

	function bindGlobalListeners() {
		// Every next/previous/submit button click, capture phase. A click logged
		// with `wasSubmitting: true` was swallowed by GF's gf_submitting guard —
		// this is the pagination-lockup signature.
		document.addEventListener(
			'click',
			function (e) {
				const id = e.target && e.target.id ? e.target.id : '';
				if (/^gform_(next|previous|submit)_button_/.test(id)) {
					const wasSubmitting = isSubmitting();
					log('button-click', { btn: id, wasSubmitting: wasSubmitting, likelySwallowed: wasSubmitting === true });
				}
			},
			true,
		);

		document.addEventListener(
			'submit',
			function (e) {
				if (e.target && e.target.id === 'gform_' + formId) {
					log('form-submit', { defaultPrevented: e.defaultPrevented });
				}
			},
			true,
		);

		// Page lifecycle — the events implicated in idle/backgrounding lockups.
		[
			['visibilitychange', document],
			['pageshow', window],
			['pagehide', window],
			['freeze', document],
			['resume', document],
			['online', window],
			['offline', window],
		].forEach(function (pair) {
			pair[1].addEventListener(
				pair[0],
				function (e) {
					log('lifecycle:' + pair[0], { persisted: e ? e.persisted : undefined });
				},
				true,
			);
		});

		// Gravity Forms namespaced (2.9) events.
		['gform/post_init', 'gform/page_loaded', 'gform/post_render'].forEach(function (evt) {
			document.addEventListener(evt, function (e) {
				log('gfevent:' + evt, { detail: safeDetail(e) });
			});
		});

		// Gravity Forms jQuery events (page swap + confirmation).
		if (window.jQuery) {
			const $ = window.jQuery;
			$(document).on('gform_pre_post_render', function (e, data) {
				log('gform_pre_post_render', { data: data ? { formId: data.formId, currentPage: data.currentPage } : null });
			});
			$(document).on('gform_page_loaded', function (e, fId, cp) {
				log('gform_page_loaded', { pageFormId: fId, currentPage: cp });
			});
			$(document).on('gform_post_render', function (e, fId, cp) {
				log('gform_post_render', { pageFormId: fId, currentPage: cp });
			});
			$(document).on('gform_confirmation_loaded', function (e, fId) {
				log('gform_confirmation_loaded', { pageFormId: fId });
			});
		}

		bindIframeWatcher();
	}

	function attach(id, componentRef) {
		if (id) {
			formId = id;
		}
		if (componentRef) {
			component = componentRef;
		}
		log('component-attach', { alreadyAttached: attached, formId: formId, jqueryPresent: !!window.jQuery });
		if (attached) {
			// Already bound the global listeners; a later call (e.g. from
			// gform/post_init once the formId is known) just re-attempts the iframe
			// watcher, which is idempotent via the frame's dataset guard.
			if (formId) {
				bindIframeWatcher();
			}
			return;
		}
		attached = true;
		bindGlobalListeners();
	}

	// Console helpers for whoever is debugging a live session.
	try {
		window.gfDiag = {
			dump: function () {
				const buffer = readBuffer();
				if (console.table) {
					console.table(buffer);
				} else {
					console.log(buffer);
				}
				return buffer;
			},
			text: function () {
				return JSON.stringify(readBuffer(), null, 2);
			},
			copy: function () {
				const text = JSON.stringify(readBuffer(), null, 2);
				try {
					// `copy` is a devtools global.
					copy(text);
					console.log('[GFDiag] Log copied to clipboard.');
				} catch (e) {
					console.log('[GFDiag] copy() is only available in devtools; here is the log:\n' + text);
				}
				return text;
			},
			clear: function () {
				writeBuffer([]);
				console.log('[GFDiag] Log cleared.');
			},
			enable: function () {
				try {
					window.localStorage.removeItem('gfDiag');
				} catch (e) {}
				enabled = true;
				console.log('[GFDiag] Console logging enabled.');
			},
			disable: function () {
				try {
					window.localStorage.setItem('gfDiag', '0');
				} catch (e) {}
				enabled = false;
				console.log('[GFDiag] Console logging disabled (buffer still captured; gfDiag.dump() to read).');
			},
		};
	} catch (e) {}

	return { attach: attach, log: log };
})();

class GetInTouchFormLegacy {
	showChildrenGuestsSelector = false;

	isLoading = false;

	currentImage = 'yellow-canyon';

	normalizeRequiredPhoneState(requiredPhone, source = 'unknown') {
		if (!requiredPhone) {
			return;
		}

		const requiredPhoneField = requiredPhone.closest('.gfield');
		const requiredPhoneValue = (requiredPhone.value || '').trim();
		const digitsCount = requiredPhoneValue.replace(/\D/g, '').length;
		const isDialCodeOnly = requiredPhoneValue.length > 0 && /^\+\d{1,4}$/.test(requiredPhoneValue);
		const isActuallyValid = !!requiredPhoneValue && !isDialCodeOnly && digitsCount >= 5;

		if (!isActuallyValid) {
			return;
		}

		requiredPhone.setAttribute('aria-invalid', 'false');
		if (requiredPhoneField) {
			requiredPhoneField.classList.remove('gfield_error');
		}
		const validationMessageNode = requiredPhoneField?.querySelector('.validation_message, .gfield_validation_message');
		if (validationMessageNode) {
			validationMessageNode.remove();
		}
		// Clear any stale intl-phone plugin error node now that the value is valid.
		const staleErrorMsg = requiredPhone.closest('.ginput_container_phone')?.querySelector('.gfipm-error-msg');
		if (staleErrorMsg) {
			staleErrorMsg.remove();
		}
	}

	/**
	 * Reads a Gravity Forms field's current value straight from the DOM by
	 * its numeric field id, checking a checked radio/checkbox first before
	 * falling back to a plain input/textarea value.
	 */
	getGfFieldRawValue(form, fieldId) {
		if (!form) {
			return '';
		}

		const name = `input_${fieldId}`;
		const checked = form.querySelector(`[name="${name}"]:checked`);
		if (checked) {
			return checked.value.trim();
		}

		const field = form.querySelector(`[name="${name}"]`);
		return field && field.value ? field.value.trim() : '';
	}

	/**
	 * Adults + teens + children + infants, per the "who is travelling" step.
	 * Field ids come from GetInTouchForm.php's fieldMapping().
	 */
	getPaxCount(form) {
		return [55, 56, 57, 58, 59].reduce((total, fieldId) => {
			const value = parseInt(this.getGfFieldRawValue(form, fieldId), 10);
			return total + (Number.isFinite(value) ? value : 0);
		}, 0);
	}

	/**
	 * Buckets the selected budget-per-person value into the LOW..TOP
	 * position label GTM expects. The field's raw value is whatever a
	 * HubSpot-synced enum property returns, which may be a "k"-shorthand
	 * range ("5-10k") or a plain absolute number - so this normalizes on
	 * the range's lower bound rather than matching exact strings.
	 */
	mapBudgetToPosition(rawValue) {
		if (!rawValue) {
			return '';
		}

		const match = rawValue.toString().match(/(\d+(?:\.\d+)?)/);
		if (!match) {
			return '';
		}

		let lowerBound = parseFloat(match[1]);
		if (lowerBound < 1000) {
			lowerBound *= 1000;
		}

		const thresholds = [
			[50000, 'TOP'],
			[20000, 'VERY_HIGH'],
			[10000, 'HIGH'],
			[5000, 'MED'],
			[2500, 'MED_LOW'],
			[1000, 'LOW'],
		];

		const bucket = thresholds.find(([threshold]) => lowerBound >= threshold);
		return bucket ? bucket[1] : 'LOW';
	}

	/**
	 * Pushes the quote wizard's conversion inputs to the dataLayer on final
	 * submit (869f245xc), and mirrors them to localStorage so GTM can still
	 * read them if the confirmation lands on a fresh pageview.
	 */
	pushQuoteConversionData(form, enquiryId) {
		if (!form) {
			return;
		}

		window.dataLayer = window.dataLayer || [];

		const alreadyPushed = window.dataLayer.some((event) => event && event.event === 'quote_form_submitted' && event.enquiryId === enquiryId);
		if (alreadyPushed) {
			return;
		}

		const budgetSelected = this.getGfFieldRawValue(form, 30);

		const conversionData = {
			conversionDestination: this.getGfFieldRawValue(form, 53),
			conversionMonth: this.getGfFieldRawValue(form, 11),
			conversionTripLength: this.getGfFieldRawValue(form, 15),
			conversionPAX: this.getPaxCount(form),
			conversionCurrency: this.getGfFieldRawValue(form, 29),
			conversionBudgetSelected: budgetSelected,
			conversionPosition: this.mapBudgetToPosition(budgetSelected),
			conversionCustomerType: this.getGfFieldRawValue(form, 50),
			conversionCall: this.getGfFieldRawValue(form, 64).toLowerCase() === 'yes',
			conversionSecondaryMessage: this.getGfFieldRawValue(form, 48),
			first_name: this.getGfFieldRawValue(form, 40),
			surname: this.getGfFieldRawValue(form, 41),
			email: this.getGfFieldRawValue(form, 42),
			// Field 72 is the raw phone input, but the intl-phone plugin renames it
			// to "input_72_phone" on init - field 98 is the hidden field it keeps in
			// sync with the combined dial code + number, so it's the reliable one to
			// read here regardless of which phone input (primary/secondary) was used.
			phone_number: this.getGfFieldRawValue(form, 98),
		};

		try {
			localStorage.setItem('atr_quote_conversion_data', JSON.stringify(conversionData));
		} catch (error) {
			// Storage can be unavailable (private browsing, quota) - the
			// dataLayer push below still carries the data for GTM.
		}

		window.dataLayer.push({
			event: 'quote_form_submitted',
			enquiryId: enquiryId,
			...conversionData,
		});
	}

	init() {
		this.isLoading = false;
		this.watchdogBound = false;
		this.clickHandlerBound = false;
		this.boundResizeHandler = null;
		this.formId = null;

		// Attach diagnostics immediately, without waiting for gform/post_init — that
		// event can fire before Alpine runs init() (a race), which would otherwise
		// leave the global listeners unbound and the buffer empty. Derive the formId
		// from the rendered form so the iframe watcher can bind right away; post_init
		// re-attaches later with the authoritative formId.
		try {
			const formEl = this.$root && this.$root.querySelector('form[id^="gform_"]');
			const derivedFormId = formEl ? formEl.id.replace('gform_', '') : null;
			if (derivedFormId) {
				this.formId = derivedFormId;
			}
			GFFormDiag.attach(derivedFormId, this);
			GFFormDiag.log('component-init', { derivedFormId: derivedFormId });
		} catch (e) {}

		window.addEventListener('pegasusformlocationupdate', (e) => {
			this.currentImage = 'locations/' + e.detail;
		});

		document.addEventListener('gform/post_init', (event) => {
			this.getImageUpdates();
			this.handleDateFields();
			this.addChildrenGuestsSelector();
			this.handlePhoneNumberInput();
			this.handleCallDateFields();

			this.isLoading = false;

			const formId = event.detail.formId;
			this.formId = formId;

			GFFormDiag.attach(formId, this);
			GFFormDiag.log('post_init-handler', { currentImage: this.currentImage });

			this.bindRecoveryWatchdog(formId);

			const enquiryInputField = this.$el.querySelector(`input[id="input_${formId}_120"]`);

			if (enquiryInputField) {
				enquiryInputField.value = document.body.getAttribute('data-enquiry-id');
			}

			this.bindNextButtonClickHandler();

			const childrenRadioGroup = this.$el.querySelectorAll('.children-selector input[type="radio"][name="input_23"]');

			if (!childrenRadioGroup || childrenRadioGroup.length === 0) {
				return;
			}

			childrenRadioGroup.forEach((radio) => {
				const radioLabel = radio.nextElementSibling;
				radioLabel.addEventListener('click', (event) => {
					this.$nextTick(() => {
						this.addChildrenGuestsSelector(true);
					});
				});
			});
		});
	}

	/**
	 * Recovers the form when Gravity Forms' `gf_submitting_{formId}` flag gets
	 * stuck (e.g. a mobile browser kills an in-flight AJAX submit when the tab
	 * is backgrounded), which otherwise silently swallows every next/previous
	 * submit. Bound once per component.
	 */
	bindRecoveryWatchdog(formId) {
		if (this.watchdogBound) {
			return;
		}
		this.watchdogBound = true;

		this.lastSubmitAt = Date.now();

		const recoverIfStuck = () => {
			const isSubmitting = window['gf_submitting_' + formId] === true;
			const stuckForMs = Date.now() - this.lastSubmitAt;

			if (isSubmitting && stuckForMs > 10000) {
				// Grace period elapsed on a stuck flag: the submission was
				// interrupted. Safe to clear and re-enable navigation.
				window['gf_submitting_' + formId] = false;
				this.isLoading = false;
				GFFormDiag.log('watchdog-cleared-stuck-flag', { stuckForMs: stuckForMs });
			} else if (isSubmitting) {
				// Still submitting but within the grace period — record that we saw
				// a submission in flight so the timeline shows how long it lingered.
				GFFormDiag.log('watchdog-in-flight', { stuckForMs: stuckForMs });
			} else if (!isSubmitting) {
				// Nothing in flight; just make sure the overlay isn't stuck up.
				this.isLoading = false;
			}
			// If submitting and still within the 10s grace period, do nothing so
			// a legitimately slow in-flight submission is never interrupted
			// (clearing too early risks a duplicate HubSpot submission).
		};

		// Measure the grace period from the last actual submit.
		document.addEventListener(
			'submit',
			(e) => {
				if (e.target && e.target.id === 'gform_' + formId) {
					this.lastSubmitAt = Date.now();
				}
			},
			true,
		);

		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				setTimeout(recoverIfStuck, 1500);
			}
		});

		window.addEventListener('pageshow', (event) => {
			if (event.persisted) {
				recoverIfStuck();
			}
		});

		window.addEventListener('online', () => {
			setTimeout(recoverIfStuck, 1500);
		});

		// Run unconditionally: the stuck `gf_submitting_{formId}` flag can persist
		// on any page transition (not just the final-next one that sets
		// `isLoading`), so gating this on `isLoading` left generic page
		// transitions unable to self-heal. `recoverIfStuck` is already safe to
		// call at any time — it only clears the flag after the 10s grace.
		setInterval(recoverIfStuck, 5000);
	}

	/**
	 * Stop a page advancing while a phone number on it is invalid.
	 *
	 * The international phone add-on shows a validation note but never blocks
	 * the Next button, so an unusable number could be carried through to
	 * submission. Its own `gfipm_valids` hidden field can't be used for this —
	 * it records a code only while the input is EMPTY and clears it as soon as
	 * anything is typed — and its error note is removed again on every keyup, so
	 * sniffing the DOM for a visible message misses a guest who types a bad
	 * number and clicks Next without leaving the field. We ask intl-tel-input
	 * itself instead, which is what the add-on validates with.
	 *
	 * @param {Element} button The Next/Submit button that was clicked.
	 * @returns {boolean} True when the click should be blocked.
	 */
	callBookingPhoneInvalid(button) {
		const form = button.closest('form');

		if (!form) {
			return false;
		}

		// Only the page being left matters; other pages are hidden.
		const page = button.closest('.gform_page') || form;
		const inputs = Array.from(page.querySelectorAll('.ginput_container_phone input[type="tel"]')).filter(
			(input) => input.offsetParent !== null
		);

		let blocked = false;

		inputs.forEach((input) => {
			const value = input.value.trim();

			if (value === '') {
				// Empty is Gravity Forms' business, not ours.
				return;
			}

			const iti = this.intlTelInstance(input);

			if (!iti) {
				// No instance to ask — fall back to the add-on's own note, which
				// is better than nothing and never blocks a valid number.
				const message = input.closest('.ginput_container_phone')?.querySelector('.gfipm-error-msg');

				if (message && message.offsetParent !== null && message.textContent.trim() !== '') {
					blocked = true;
				}

				return;
			}

			const valid = typeof iti.isValidNumberPrecise === 'function'
				? iti.isValidNumberPrecise()
				: typeof iti.isValidNumber === 'function' && iti.isValidNumber();

			if (valid) {
				return;
			}

			blocked = true;

			// Make the add-on redraw its own message, so the guest sees why they
			// are being held up rather than a button that silently does nothing.
			input.dispatchEvent(new FocusEvent('blur'));

			if (!this.callBookingPhoneFocused) {
				this.callBookingPhoneFocused = true;
				input.focus();
				window.setTimeout(() => {
					this.callBookingPhoneFocused = false;
				}, 0);
			}
		});

		return blocked;
	}

	/**
	 * The intl-tel-input instance for a field, across both the v17 and v18 APIs.
	 */
	intlTelInstance(input) {
		try {
			if (window.intlTelInput && typeof window.intlTelInput.getInstance === 'function') {
				return window.intlTelInput.getInstance(input);
			}

			if (window.intlTelInputGlobals && typeof window.intlTelInputGlobals.getInstance === 'function') {
				return window.intlTelInputGlobals.getInstance(input);
			}
		} catch (error) {
			// Plugin missing or mid-teardown; treat as "can't tell".
		}

		return null;
	}

	/**
	 * Give `target` the same country as `source`.
	 *
	 * The contact-details phone (field 72) and the call-booking phone (field 127)
	 * hold one number, but each has its own intl-tel-input picker, and 127 keeps
	 * the geo-lookup default. A Canadian number then reads as "+44…" on 127.
	 */
	copyPhoneCountry(source, target) {
		const sourceIti = source && this.intlTelInstance(source);
		const targetIti = target && this.intlTelInstance(target);
		const iso2 = sourceIti?.getSelectedCountryData()?.iso2;

		if (!iso2 || !targetIti || targetIti.getSelectedCountryData()?.iso2 === iso2) {
			return;
		}

		targetIti.setCountry(iso2);
	}

	/**
	 * The number with its dial code ("+447445200632"), or '' while empty.
	 */
	phoneFullNumber(input) {
		const value = (input?.value || '').trim();

		if (!value) {
			return '';
		}

		const number = this.intlTelInstance(input)?.getNumber() || '';

		if (number.startsWith('+')) {
			return number;
		}

		const dialCode = input.closest('.ginput_container_phone')?.querySelector('.iti__selected-dial-code')?.textContent?.trim() || '';

		return dialCode + value.replace(/\s+/g, '');
	}

	/**
	 * Write the full number into each phone field's hidden value, which is what
	 * Gravity Forms stores and HubSpot is sent.
	 *
	 * HubSpot gets the enquiry twice: leaving the contact-details page, then on
	 * final submit, where field 127 replaces the phone. Its hidden value used to
	 * be the bare number shown on screen ("7445 200632"), which the automated
	 * texts can't use.
	 */
	syncPhoneHiddenFields() {
		['72', '127'].forEach((fieldId) => {
			const input = this.$root.querySelector(`input[name='input_${fieldId}_phone']`);
			const hidden = this.$root.querySelector(`input[name='input_${fieldId}']`);
			const hiddenCountry = this.$root.querySelector(`input[name='input_${fieldId}_country_code']`);

			// An empty input during a re-render isn't the guest clearing it, so
			// keep whatever the hidden value already holds.
			if (!input || !hidden || !input.value.trim()) {
				return;
			}

			hidden.value = this.phoneFullNumber(input);

			const iso2 = this.intlTelInstance(input)?.getSelectedCountryData()?.iso2;
			if (hiddenCountry && iso2) {
				hiddenCountry.value = iso2;
			}
		});

		// Field 96 is sent to HubSpot as the mobile country code. It was only set
		// on render, so the first send carried the page's default country.
		const primaryPhone = this.$root.querySelector("input[name='input_72_phone']");
		const countryName = primaryPhone?.value.trim()
			? primaryPhone.closest('.ginput_container_phone')?.querySelector('button.iti__selected-country')?.getAttribute('title')
			: null;
		const hiddenCountryCode = this.$root.querySelector("input[name='input_96']");
		if (hiddenCountryCode && countryName && countryName !== 'Unknown') {
			hiddenCountryCode.value = countryName;
		}
	}

	/**
	 * Capture-phase document click listener that validates page 7 before
	 * allowing the next button. Bound once; reads the current formId/DOM at
	 * click time rather than from a stale closure.
	 */
	bindNextButtonClickHandler() {
		if (this.clickHandlerBound) {
			return;
		}
		this.clickHandlerBound = true;

		const _this = this;

		// Covers a page posted without a button click (Enter in a field).
		document.addEventListener(
			'submit',
			function (e) {
				if (_this.$root.contains(e.target)) {
					_this.syncPhoneHiddenFields();
				}
			},
			true,
		);

		document.addEventListener(
			'click',
			function (e) {
				if (!_this.$root.contains(e.target)) {
					return;
				}

				const formId = _this.formId;

				if (!formId) {
					return;
				}

				// Each Next/Submit posts both phone fields and HubSpot is sent what
				// they hold, so write the full number into them first. The phone
				// add-on restores stale values from sessionStorage on every render.
				if (e.target && e.target.closest && e.target.closest('.gform_next_button, .gform_button[type="submit"]')) {
					_this.syncPhoneHiddenFields();
				}

				if (e.target && e.target.id && e.target.id.startsWith(`gform_next_button_${formId}_`)) {
					const requiredPhone = _this.$root.querySelector(`#input_${formId}_127`);
					_this.normalizeRequiredPhoneState(requiredPhone, 'next-button-click');

					if (_this.callBookingPhoneInvalid(e.target)) {
						GFFormDiag.log('validation-block', { btn: e.target.id, reason: 'invalid phone number' });
						e.stopImmediatePropagation();
						e.preventDefault();
						return;
					}
				}

				const isSubmitButton =
					e.target &&
					e.target.matches &&
					e.target.matches(
						`input.gform_button[type="submit"][id^="gform_submit_button_${formId}"], button.gform_button[type="submit"][id^="gform_submit_button_${formId}"]`,
					);

				if (isSubmitButton) {
					const form = e.target.closest('form');
					const enquiryId = document.body.getAttribute('data-enquiry-id');
					_this.pushQuoteConversionData(form, enquiryId);
				}

				if (e.target && e.target.id === `gform_next_button_${formId}_46`) {
					const form = e.target.closest('form');

					// check current page fields are valid
					const currentPageFields = form.querySelectorAll(
						`#gform_page_${formId}_7 .gfield input[name="input_72_phone"][aria-required="true"], #gform_page_${formId}_7 .gfield select, #gform_page_${formId}_7 .gfield input[aria-required="true"]`,
					);

					let isValid = true;

					currentPageFields.forEach((field) => {
						if (field && !field.value) {
							isValid = false;
						}

						if (field && field.getAttribute('type') === 'tel') {
							const errMsg = field.closest('.ginput_container_phone')?.querySelector('.gfipm-error-msg');

							if (errMsg && errMsg.offsetParent !== null && errMsg.textContent.trim() !== '') {
								isValid = false;
							}
						}

						if (field && field.getAttribute('type') === 'email') {
							// Check if the email is valid
							field.checkValidity();

							if (!field.validity.valid) {
								isValid = false;
							}
						}
					});

					if (!isValid) {
						GFFormDiag.log('validation-block', { btn: e.target.id, reason: 'page-7 fields invalid' });
						e.stopImmediatePropagation();
						e.preventDefault();
						return;
					}

					_this.isLoading = true;
					window.dataLayer = window.dataLayer || [];

					const enquiryId = document.body.getAttribute('data-enquiry-id');

					// If enquiryId already exists in the data layer don't push it again
					const existingEnquiryEvent = window.dataLayer.find((event) => event.event === 'enquiry_submitted' && event.enquiryId === enquiryId);

					if (!existingEnquiryEvent) {
						window.dataLayer.push({
							event: 'enquiry_submitted',
							enquiryId: enquiryId,
						});
					}
				}
			},
			true,
		);
	}

	goBackToForm(formId) {
		var prevPage = localStorage.getItem('gf_prev_page_' + formId) || 1;
		var formUrl = window.location.href.split('?')[0] + '?gf_page=' + prevPage;
		window.location.href = formUrl;
	}

	addChildrenGuestsSelector() {
		const childRadioElements = this.$root.querySelectorAll('.children-selector input[type="radio"][name="input_23"]');
		const addChildrenSelector = this.$root.querySelector('.children-selector input[type="radio"][name="input_23"][value="Add children"]').parentElement;

		childRadioElements.forEach((radio) => {
			if (radio.value === 'Add children' && radio.checked) {
				this.showChildrenGuestsSelector = true;
			}
		});

		if (this.showChildrenGuestsSelector) {
			addChildrenSelector.style.display = 'none';
		} else {
			addChildrenSelector.style.display = '';
		}
	}

	handleNumberFields() {
		this.$root.querySelectorAll('.ginput_container_number').forEach((input) => {
			const number = input.querySelector('input[type="number"]');
			//set the number value to the minimum value
			input.addEventListener('click', function (event) {
				const rect = this.getBoundingClientRect();
				const clickX = event.clientX - rect.left;

				// Check if the click is on the left or right of the input
				if (clickX < 30) {
					number.stepDown(1);
					// number.value = number.value;
				} else if (clickX > rect.width - 30) {
					// Increment (right side)
					number.stepUp(1);
					// number.value = number.value;
				}

				// Trigger change event
				number.dispatchEvent(new Event('change', { bubbles: true }));
				number.dispatchEvent(
					new CustomEvent('gform_input_change', {
						bubbles: true,
						detail: { field: number },
					}),
				);
			});
		});
	}

	getImageUpdates() {
		const form = this.$root.querySelector('form');

		if (!form) {
			return;
		}

		const formData = new FormData(form);

		const locationField = formData.getAll('input_53');

		if (locationField.length === 0) {
			return;
		}

		window.dispatchEvent(
			new CustomEvent('pegasusformlocationupdate', {
				detail: locationField[0].replaceAll(' ', '-'),
			}),
		);
	}

	handleDateFields() {
		let checkedMonthRadio = this.$root.querySelector('.gfield input[type="radio"][name="input_11"]:checked');

		const date = new Date();
		const currentYear = date.getFullYear().toString().slice(-2);

		const moreMonths = this.$root.querySelector('.more-months');

		if (checkedMonthRadio && parseInt(this.getDateArray(checkedMonthRadio)[1]) >= parseInt(currentYear) + 2) {
			this.showMonths();
			moreMonths.style.display = 'none';
		} else {
			this.hideMonths();
		}

		this.handleMoreMonths();

		this.updateMonths();
	}

	getMonthsHeight() {
		const monthsField = this.$root.querySelector('.months-field');

		if (!monthsField) {
			return;
		}

		const radioGroup = monthsField.querySelector('.gfield_radio');

		if (!radioGroup || radioGroup.length === 0) {
			return;
		}

		radioGroup.style.height = '';

		const radioGroupHeight = radioGroup.offsetHeight;

		return radioGroupHeight;
	}

	hideMonths() {
		const monthsField = this.$root.querySelector('.months-field');

		if (!monthsField) {
			return;
		}

		const radioGroup = monthsField.querySelector('.gfield_radio');

		if (!radioGroup || radioGroup.length === 0) {
			return;
		}

		let monthsHeight = this.getMonthsHeight();

		radioGroup.classList.add('overflow-hidden');

		radioGroup.style.height = `${monthsHeight / 2}px`;

		if (this.boundResizeHandler) {
			window.removeEventListener('resize', this.boundResizeHandler);
		}
		this.boundResizeHandler = () => {
			monthsHeight = this.getMonthsHeight();
			radioGroup.style.height = `${monthsHeight / 2}px`;
		};
		window.addEventListener('resize', this.boundResizeHandler);
	}

	showMonths() {
		const monthsField = this.$root.querySelector('.months-field');

		if (!monthsField) {
			return;
		}

		const radioGroup = monthsField.querySelector('.gfield_radio');

		if (!radioGroup || radioGroup.length === 0) {
			return;
		}

		let monthsHeight = this.getMonthsHeight();

		radioGroup.style.height = `${monthsHeight}px`;

		if (this.boundResizeHandler) {
			window.removeEventListener('resize', this.boundResizeHandler);
		}
		this.boundResizeHandler = () => {
			monthsHeight = this.getMonthsHeight();
			radioGroup.style.height = `${monthsHeight}px`;
		};
		window.addEventListener('resize', this.boundResizeHandler);
	}

	handleMoreMonths() {
		const moreMonths = this.$root.querySelector('.more-months');
		const input = moreMonths.querySelector('input[type="radio"]');

		if (!input) {
			return;
		}

		if (input.checked) {
			this.showMonths();

			moreMonths.remove();
		}

		input.addEventListener('change', (event) => {
			if (input.checked) {
				this.showMonths();

				moreMonths.remove();
			}
		});
	}

	updateMonths() {
		const monthRadios = this.$root.querySelectorAll('.gfield input[type="radio"][name="input_11"]');

		if (!monthRadios || monthRadios.length === 0) {
			return;
		}

		const date = new Date();
		const currentYear = date.getFullYear().toString().slice(-2);

		// Loop through month radios and if it's value is not available for the selected year, disabled it and lower opacity otherwise enable it
		monthRadios.forEach((radio) => {
			const dateArray = this.getDateArray(radio);
			if (
				!this.isMonthAvailable(dateArray[1], dateArray[0]) ||
				(parseInt(dateArray[1]) >= parseInt(currentYear) + 3 && this.isMonthAvailable(currentYear, dateArray[0]))
			) {
				radio.classList.add('disabled');
				// radio.setAttribute('disabled', 'disabled');
			} else {
				// radio.removeAttribute('disabled');
				radio.classList.remove('disabled');
			}

			radio.addEventListener('change', (event) => {
				const yearField = this.$root.querySelector('.gfield input[type="hidden"][name="input_115"]');

				const monthField = this.$root.querySelector('.gfield input[type="hidden"][name="input_119"]');

				if (!yearField || !monthField) {
					return;
				}

				const selectedYear = '20' + dateArray[1];

				yearField.value = selectedYear;
				monthField.value = dateArray[0];
			});
		});
	}

	getDateArray(radio) {
		// Get next sibling of radio
		const label = radio.nextElementSibling;

		// get text content of label
		const date = label.textContent;

		const dateArray = date.split(' ');

		Object.entries(this.getMonths()).forEach(([key, value]) => {
			if (dateArray[0] === value) {
				dateArray[0] = key;
			}
		});

		// Split date by space
		return dateArray;
	}

	getMonths() {
		return {
			January: 'Jan',
			February: 'Feb',
			March: 'Mar',
			April: 'Apr',
			May: 'May',
			June: 'Jun',
			July: 'Jul',
			August: 'Aug',
			September: 'Sep',
			October: 'Oct',
			November: 'Nov',
			December: 'Dec',
		};
	}

	getAvailableMonthsLeftInCurrentYear() {
		let months = [];
		let currentMonth = new Date().getMonth();
		let monthsLeft = 12 - currentMonth;
		for (let i = 0; i < monthsLeft; i++) {
			months.push(Object.keys(this.getMonths())[currentMonth + i]);
		}
		return months;
	}

	isMonthAvailable(year, month) {
		const date = new Date();
		const currentYear = date.getFullYear().toString().slice(-2);
		if (year == currentYear) {
			return this.getAvailableMonthsLeftInCurrentYear().includes(month);
		}

		return true;
	}

	handlePhoneNumberInput() {
		const phoneInput = this.$root.querySelectorAll("input[type='tel']");

		const hiddenCountryCode = this.$root.querySelector("input[name='input_96']");
		const hiddenPhone = this.$root.querySelector("input[name='input_98']");
		const selectedCountryButton = this.$root.querySelector('button.iti__selected-country');
		const selectedCodeElement = this.$root.querySelector('button.iti__selected-country .iti__selected-dial-code');

		if (!phoneInput || !hiddenCountryCode || !hiddenPhone || !selectedCountryButton || !selectedCodeElement) {
			return;
		}

		const selectedCountry = selectedCountryButton.getAttribute('title');
		const selectedCode = selectedCodeElement.textContent;

		if (phoneInput[0].value !== '' && phoneInput[0].value !== undefined) {
			if (phoneInput[1] !== undefined) {
				this.copyPhoneCountry(phoneInput[0], phoneInput[1]);
				phoneInput[1].value = phoneInput[0].value;
				phoneInput[1].dispatchEvent(new Event('blur', { bubbles: true }));
			}
		}

		if (phoneInput[1]) {
			phoneInput[1].addEventListener('blur', (event) => {
				if (phoneInput[1].value !== '' && phoneInput[1].value !== undefined) {
					if (phoneInput[0] !== undefined) {
						this.copyPhoneCountry(phoneInput[1], phoneInput[0]);
						phoneInput[0].value = phoneInput[1].value;
						phoneInput[0].dispatchEvent(new Event('input', { bubbles: true }));
						phoneInput[0].dispatchEvent(new Event('change', { bubbles: true }));
					}
				}
				this.syncPhoneHiddenFields();
				this.normalizeRequiredPhoneState(this.$root.querySelector("input[name='input_127_phone']"), 'phone2-blur');
			});
			phoneInput[1].addEventListener('input', () => {
				this.syncPhoneHiddenFields();
				this.normalizeRequiredPhoneState(this.$root.querySelector("input[name='input_127_phone']"), 'phone2-input');
			});
		}

		hiddenPhone.id = 'phone';

		if (selectedCountry !== 'Unknown') {
			hiddenCountryCode.value = selectedCountry;
		}

		hiddenPhone.value = selectedCode + (phoneInput[0]?.value || '');
		this.syncPhoneHiddenFields();

		const intlPhoneInput = this.$root.querySelectorAll('input[type="tel"]');

		if (!intlPhoneInput) {
			return;
		}

		intlPhoneInput.forEach((input) => {
			input.addEventListener('input', (event) => {
				// If the first number is a 0, remove it
				if (input.value.startsWith('0')) {
					input.value = input.value.slice(1);
				}
			});

			if (input.dataset.phoneCountrySyncBound === 'true') {
				return;
			}
			input.dataset.phoneCountrySyncBound = 'true';

			// Both fields hold one number, so a country picked on either applies to both.
			// An empty field's change is the geo-lookup default, not the guest's pick,
			// and must not overwrite the other field. copyPhoneCountry's own
			// setCountry fires this again on the other field, which then stops.
			input.addEventListener('countrychange', () => {
				if (!input.value.trim()) {
					return;
				}

				const other = input === phoneInput[0] ? phoneInput[1] : phoneInput[0];
				this.copyPhoneCountry(input, other);
				this.syncPhoneHiddenFields();
			});

			// An edit to the contact-details phone must reach the call-booking one
			// straight away. It's only mirrored on render otherwise, so after a
			// validation error or Back, field 127 kept the old number and, being
			// preferred by the server, sent it to HubSpot on leaving this page.
			// Real typing only: the call-booking blur copies its value here and
			// dispatches a synthetic input, which mustn't bounce back.
			if (input === phoneInput[0] && phoneInput[1]) {
				input.addEventListener('input', (event) => {
					if (!event.isTrusted) {
						return;
					}

					this.copyPhoneCountry(input, phoneInput[1]);
					phoneInput[1].value = input.value;

					// syncPhoneHiddenFields() leaves an empty field's hidden value
					// alone, so clear them here or the old number would still post.
					if (!input.value.trim()) {
						['input_72', 'input_127'].forEach((name) => {
							const hidden = this.$root.querySelector(`input[name='${name}']`);
							if (hidden) {
								hidden.value = '';
							}
						});
					}

					this.syncPhoneHiddenFields();
				});
			}
		});
		this.normalizeRequiredPhoneState(this.$root.querySelector("input[name='input_127_phone']"), 'handlePhoneNumberInput-end');
	}

	/**
	 * Call booking: keep the date/time selection inside the bookable window and
	 * show the guest what their choice means in their own timezone.
	 *
	 * The stored values are UK wall-clock time — that is what the team reads —
	 * but a guest in Los Angeles reads the dropdown in Pacific time and used to
	 * be able to request a slot that had already passed here. So we disable
	 * everything that isn't bookable (past days, weekends, closed dates, slots
	 * outside the calling window) and relabel every slot with both times.
	 *
	 * Re-runs safely: everything is derived from option *values*, never from the
	 * labels we have already written, so repeated gform/post_init events and GF
	 * paging re-renders are harmless.
	 */
	handleCallDateFields() {
		const elements = this.callBookingElements();

		if (!elements) {
			return;
		}

		// Listen on the component root, not on the selects themselves. The form
		// pages via Gravity Forms AJAX, which replaces its whole markup on every
		// Next click — by the call step the date/time selects have been swapped
		// out eight times. Listeners bound to the originals died with them, so
		// the initial marking still ran on each render but changing the month
		// never re-marked the days: pick October on the 23rd of September and
		// you got September's pattern, with the 1st-7th disabled. The root is
		// ours, not GF's, so one delegated listener survives every re-render;
		// each change re-resolves the fields to see whether it was one of them.
		if (!this.callBookingBound) {
			this.callBookingBound = true;

			this.$root.addEventListener('change', (event) => {
				const current = this.callBookingElements();

				if (!current) {
					return;
				}

				const controls = [current.year, current.month, current.day, current.time, current.picker];

				if (controls.includes(event.target)) {
					this.refreshCallBooking();
				}
			});
		}

		// Never block first paint on a third-party request: the device's timezone
		// labels the form now, and if the IP lookup says the guest is elsewhere
		// we relabel in place. Runs once per session, whatever it returns.
		if (!this.callBookingGeoRequested) {
			this.callBookingGeoRequested = true;
			lookupGuestGeo(elements.config).then((geo) => {
				if (geo) {
					this.refreshCallBooking();
					this.fillCallBookingLocation();
				}
			});
		}

		this.fillCallBookingLocation();

		this.refreshCallBooking();
	}

	/**
	 * Resolve the call date/time controls.
	 *
	 * Resolution is structural — see findCallBookingFields(). Nothing here keys
	 * off a field id, a form id or an editor-set class, so renumbering the
	 * fields or adding another Start Planning variant doesn't break it.
	 *
	 * @returns {object|null}
	 */
	callBookingElements() {
		if (!this.$root) {
			return null;
		}

		if (!this.callBookingConfig) {
			this.callBookingConfig = readCallBookingConfig(this.$root);
		}

		const fields = findCallBookingFields(this.$root);

		if (!fields.container && !fields.time) {
			return null;
		}

		return { config: this.callBookingConfig, ...fields };
	}

	refreshCallBooking() {
		// Snapping an invalid selection fires change events, which land back
		// here; one pass is enough.
		if (this.callBookingSyncing) {
			return;
		}

		const elements = this.callBookingElements();

		if (!elements) {
			return;
		}

		this.callBookingSyncing = true;

		try {
			const { config } = elements;
			const earliest = earliestBookableDate(config);
			const latest = latestBookableDate(config);

			// Settle on one date first, then mark the dropdowns around it. Doing
			// it the other way — marking year, then month, then day, each step
			// snapping the one before — meant picking a year the current month
			// doesn't exist in threw the guest back to the earliest date, so
			// January was unreachable from December.
			const selectedDate = this.resolveCallBookingDate(elements, earliest, latest);
			const [targetYear, targetMonth, targetDay] = selectedDate.split('-').map(Number);

			this.updateCallBookingYears(elements, earliest, latest);
			this.setCallBookingSelect(elements.year, String(targetYear));

			this.updateCallBookingMonths(elements, earliest, latest, targetYear);
			this.setCallBookingSelect(elements.month, String(targetMonth));

			this.markCallBookingDays(elements, earliest, latest, targetYear, targetMonth);
			this.setCallBookingSelect(elements.day, String(targetDay));

			this.updateCallBookingTimes(elements, selectedDate);
			this.applyCallBookingTimeDefault(elements, selectedDate);
			this.renderCallBookingNote(elements, selectedDate);
			this.syncCallBookingGuestTime(elements, selectedDate);
		} finally {
			this.callBookingSyncing = false;
		}
	}

	/**
	 * Enable/disable one option, keeping the `.disabled` class the styles use.
	 */
	setCallBookingOptionState(option, enabled) {
		option.disabled = !enabled;
		option.classList.toggle('disabled', !enabled);
	}

	/**
	 * The date the dropdowns should settle on, honouring as much of the guest's
	 * choice as the window allows.
	 *
	 * Falls back in order: their exact date, the first bookable day in the month
	 * they picked, the first bookable day in the year they picked, and finally
	 * the earliest bookable date. So changing the year to one where their
	 * current month is out of range moves them to a month that works in that
	 * year rather than undoing the change.
	 *
	 * @returns {string} Y-m-d
	 */
	resolveCallBookingDate(elements, earliest, latest) {
		const { config } = elements;

		const bookable = (y, m, d) => {
			const isoDate = toIsoDate(y, m, d);

			return isoDate >= earliest && isoDate <= latest && isOpenOn(config, y, m, d);
		};

		const firstDayIn = (y, m) => {
			const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();

			for (let d = 1; d <= daysInMonth; d++) {
				if (bookable(y, m, d)) {
					return d;
				}
			}

			return 0;
		};

		// The datepicker variant has one input rather than three selects, so
		// there is no partial state to rescue — keep the guest's date if the
		// window allows it, otherwise start them at the earliest.
		if (!elements.day && elements.picker) {
			const picked = this.selectedCallDate(elements);

			if (picked) {
				const [py, pm, pd] = picked.split('-').map(Number);

				if (bookable(py, pm, pd)) {
					return picked;
				}
			}

			return earliest;
		}

		const year = Number(elements.year?.value) || 0;
		const month = Number(elements.month?.value) || 0;
		const day = Number(elements.day?.value) || 0;

		if (year && month && day && bookable(year, month, day)) {
			return toIsoDate(year, month, day);
		}

		if (year && month) {
			const firstDay = firstDayIn(year, month);

			if (firstDay) {
				return toIsoDate(year, month, firstDay);
			}
		}

		if (year) {
			for (let m = 1; m <= 12; m++) {
				const firstDay = firstDayIn(year, m);

				if (firstDay) {
					return toIsoDate(year, m, firstDay);
				}
			}
		}

		return earliest;
	}

	/**
	 * Only the years the bookable window actually touches. Next year shows up
	 * only once the window reaches into it — i.e. in December.
	 */
	updateCallBookingYears(elements, earliest, latest) {
		if (!elements.year) {
			return;
		}

		const earliestYear = Number(earliest.split('-')[0]);
		const latestYear = Number(latest.split('-')[0]);

		this.ensureCallBookingYearOptions(elements.year, earliestYear, latestYear);

		Array.from(elements.year.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const year = Number(option.value);

			this.setCallBookingOptionState(option, year >= earliestYear && year <= latestYear);
		});

		if (elements.year.value !== '' && elements.year.selectedOptions[0]?.disabled) {
			this.setCallBookingSelect(elements.year, String(earliestYear));
		}
	}

	/**
	 * Grow the year dropdown to cover a year it doesn't already offer.
	 *
	 * Day (1-31) and month (1-12) are always rendered in full by Gravity Forms,
	 * so any date within them can be reached just by enabling the right option.
	 * Year is different: GF only renders between gform_date_min_year and
	 * gform_date_max_year, both computed from the REAL server clock the moment
	 * the page was rendered. A page that was live when the window sat entirely
	 * in one year literally has no "next year" option in its markup — no amount
	 * of enabling/disabling reaches a year that was never rendered.
	 *
	 * That gap is real, not just a test artefact: a page cached across a
	 * year-end, or a guest revisiting a tab left open from before New Year,
	 * both leave the dropdown one option short of where the window now sits.
	 * So this adds whatever's missing rather than assuming the render always
	 * has what today's window needs.
	 */
	ensureCallBookingYearOptions(select, earliestYear, latestYear) {
		const known = new Set(Array.from(select.options).map((option) => option.value));

		for (let year = earliestYear; year <= latestYear; year++) {
			const value = String(year);

			if (known.has(value)) {
				continue;
			}

			const option = document.createElement('option');
			option.value = value;
			option.textContent = value;

			// GF lists years highest first; insert before the first existing
			// option that should come after ours, so a newly-added year lands
			// where it would have if the page had rendered it to begin with.
			const before = Array.from(select.options).find((existing) => existing.value !== '' && Number(existing.value) < year);

			select.insertBefore(option, before || null);
		}
	}

	/**
	 * Only the months the window touches — the earliest bookable month and the
	 * next one. Past months of this year go dark, and so does anything further
	 * ahead than we'll take an enquiry call.
	 */
	updateCallBookingMonths(elements, earliest, latest, selectedYear) {
		if (!elements.month) {
			return;
		}

		Array.from(elements.month.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const month = Number(option.value);
			const lastDay = new Date(Date.UTC(selectedYear, month, 0)).getUTCDate();

			// A month is open if any day in it falls inside the window.
			const opens = toIsoDate(selectedYear, month, lastDay) >= earliest;
			const closes = toIsoDate(selectedYear, month, 1) <= latest;

			this.setCallBookingOptionState(option, opens && closes);
		});
	}

	/**
	 * Mark each day option bookable or not for the given month. Marking only —
	 * resolveCallBookingDate() decides what ends up selected.
	 */
	markCallBookingDays(elements, earliest, latest, year, month) {
		const { config } = elements;
		const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

		Array.from(elements.day.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const day = Number(option.value);

			if (day > daysInMonth) {
				this.setCallBookingOptionState(option, false);
				return;
			}

			const isoDate = toIsoDate(year, month, day);
			const bookable =
				isoDate >= earliest && isoDate <= latest && isOpenOn(config, year, month, day);

			this.setCallBookingOptionState(option, bookable);
		});
	}

	/**
	 * Relabel every slot with both timezones and disable the ones we can't take.
	 */
	updateCallBookingTimes(elements, isoDate) {
		if (!elements.time) {
			return;
		}

		const { config } = elements;
		const now = Date.now();

		Array.from(elements.time.options).forEach((option) => {
			if (option.value === '') {
				return;
			}

			const minutes = slotToMinutes(option.value);

			if (minutes === null) {
				return;
			}

			const slot = describeSlot(config, isoDate, minutes);
			const inWindow = minutes >= config.firstSlot && minutes <= config.lastSlot;

			this.setCallBookingOptionState(option, inWindow && slot.instant.getTime() > now);
			option.textContent = slotOptionLabel(config, slot);
		});

		if (elements.time.value !== '' && elements.time.selectedOptions[0]?.disabled) {
			const firstBookable = Array.from(elements.time.options).find(
				(option) => option.value !== '' && !option.disabled
			);

			if (firstBookable) {
				this.setCallBookingSelect(elements.time, firstBookable.value);
			}
		}
	}

	/**
	 * Set a select's value and tell everyone else — GF's conditional logic and
	 * our own listeners both watch for change.
	 */
	setCallBookingSelect(select, value) {
		if (!select || select.value === value) {
			return;
		}

		select.value = value;
		select.dispatchEvent(new Event('input', { bubbles: true }));
		select.dispatchEvent(new Event('change', { bubbles: true }));
	}

	/**
	 * The currently selected call date as Y-m-d, or null while incomplete.
	 */
	selectedCallDate(elements) {
		const year = Number(elements.year?.value);
		const month = Number(elements.month?.value);
		const day = Number(elements.day?.value);

		if (!year || !month || !day) {
			return null;
		}

		return toIsoDate(year, month, day);
	}

	/**
	 * The line under the fields that spells the booking out in both timezones —
	 * the thing that stops a guest assuming "10:00am" means their morning.
	 */
	/**
	 * Pre-select 10am in the guest's own timezone, per the client's brief.
	 *
	 * PHP can only offer a UK-time placeholder — the page is cached, so it can't
	 * know where the guest is — so the browser corrects it once it does. We only
	 * touch the field while it still holds that placeholder or a value we set
	 * ourselves; a slot the guest chose, or one restored from their last
	 * enquiry, is left alone. Re-runs when the IP lookup lands and moves the
	 * guest to a different timezone than their device claimed.
	 */
	applyCallBookingTimeDefault(elements, isoDate) {
		const { config } = elements;

		if (!elements.time) {
			return;
		}

		const zone = guestTimeZone();

		if (this.callBookingDefaultZone === zone) {
			return;
		}

		const current = elements.time.value;
		const ours = current === '' || current === config.defaultSlot || current === this.callBookingAutoTime;

		this.callBookingDefaultZone = zone;

		if (!ours) {
			return;
		}

		const best = slotNearestLocalTime(config, isoDate, config.defaultLocalMinutes);

		if (best && best.value !== current) {
			this.callBookingAutoTime = best.value;
			this.setCallBookingSelect(elements.time, best.value);
		}
	}

	/**
	 * Keep the hidden "their time" field in step with the time dropdown.
	 *
	 * The dropdown submits UK time — its values are the UK slots, only the
	 * labels are converted — so the guest's own time would otherwise be lost
	 * once they leave the page. Written on every refresh, so it follows the
	 * guest's latest choice and the IP lookup's timezone correction. The field
	 * is found by the class PHP puts on it, never by id; if it hasn't been added
	 * to the form yet this does nothing and the entry just lacks their side.
	 */
	syncCallBookingGuestTime(elements, isoDate) {
		const input = this.$root.querySelector('.pegasus-guest-call-time input');

		if (!input) {
			return;
		}

		const minutes = elements.time ? slotToMinutes(elements.time.value) : null;
		const value = minutes === null ? '' : guestTimeDescription(describeSlot(elements.config, isoDate, minutes));

		if (input.value !== value) {
			input.value = value;
		}
	}

	/**
	 * Suggest the guest's location from the IP lookup.
	 *
	 * This used to be done in PHP during render, which on a page-cached site
	 * meant one visitor's region was baked into the HTML and served to everyone
	 * after them — the reason it looked broken. Doing it here is per-visitor and
	 * costs no extra request: it's the same ipinfo response the phone field and
	 * the timezone labels already use.
	 *
	 * Only ever fills a blank field, so anything the guest types — or anything
	 * restored from their last enquiry — stands.
	 */
	fillCallBookingLocation() {
		if (!this.$root || this.callBookingLocationFilled) {
			return;
		}

		const input = this.$root.querySelector('.pegasus-guest-location input');

		if (!input || input.value.trim() !== '') {
			return;
		}

		const place = guestPlace();

		if (place === '') {
			return;
		}

		this.callBookingLocationFilled = true;
		input.value = place;
		input.dispatchEvent(new Event('input', { bubbles: true }));
		input.dispatchEvent(new Event('change', { bubbles: true }));
	}

	renderCallBookingNote(elements, isoDate) {
		const { config } = elements;

		if (!elements.container && !elements.time) {
			return;
		}

		const host = (elements.container || elements.time.closest('.gfield'))?.closest('.gform_fields');

		if (!host) {
			return;
		}

		let note = host.querySelector('.call-booking-note');

		if (!note) {
			note = document.createElement('div');
			note.className = 'call-booking-note';
			note.setAttribute('aria-live', 'polite');
			host.appendChild(note);
		}

		const timeValue = elements.time ? elements.time.value : '';
		const minutes = slotToMinutes(timeValue);

		if (minutes === null) {
			note.textContent = 'Times are shown in your own timezone — pick one that suits you.';
			return;
		}

		const slot = describeSlot(config, isoDate, minutes);
		const siteZone = timeZoneAbbreviation(config.timezone, slot.instant);
		const guestZone = guestZoneAbbreviation(slot.guestZone, slot.instant);

		// The guest's own day, which is the one they care about, and can be the
		// day after ours for a slot late in our evening.
		const guestDate = this.formatCallBookingDate(slot.dayShift === 0 ? isoDate : slot.localIsoDate);

		// The reminder to fix a wrong auto-filled location applies whichever
		// branch we're in below — a UK guest travelling abroad needs it just as
		// much as an overseas one.
		const locationReminder = 'Kindly update your location if this is not the location you will be in at the time of the call.';

		if (slot.matchesSite) {
			note.textContent =
				`We'll call you on ${guestDate} at ${slot.localLabel}${siteZone ? ` (${siteZone})` : ''}. ` +
				locationReminder;
			return;
		}

		// The city the IP lookup gave us reads better than the timezone's name:
		// a guest in Boston is on New York time, but "Boston" is what they'd
		// recognise as where they are.
		const place = guestPlace() || prettyTimeZoneName(slot.guestZone);

		note.textContent =
			`Times are shown in your own timezone${place ? `, ${place}` : ''}. ` +
			`We'll call you on ${guestDate} at ${slot.localLabel}${guestZone ? ` ${guestZone}` : ''}. ` +
			locationReminder;
	}

	/**
	 * "Monday 24 August" from a Y-m-d string, with no timezone shifting.
	 */
	formatCallBookingDate(isoDate) {
		const [year, month, day] = isoDate.split('-').map(Number);

		return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
			timeZone: 'UTC',
		});
	}

	goToLastPage() {
		if (pegasus.getCookie('lastPage')) {
			window.location.href = pegasus.getCookie('lastPage');
		} else {
			window.location.href = '/';
		}
	}
}

document.addEventListener("alpine:init", () => {
    Alpine.data("getInTouchFormLegacy", () => new GetInTouchFormLegacy());
});
